/**
 * Minimal in-memory read cache for reference/master data that is
 * identical across many pages (items, uoms, warehouses, suppliers,
 * branches) — a source-level performance audit found every operational
 * page independently re-fetches this same data from scratch on every
 * mount, with zero reuse across navigations (Dashboard -> Items ->
 * Suppliers -> PR -> ... each re-requests items/uoms/warehouses even
 * though nothing changed). This cuts those redundant round trips without
 * touching RLS or server-side authorization — every cached response
 * already passed through the real, permission-scoped Supabase call once;
 * this only avoids repeating an identical request within a short TTL.
 *
 * Deliberately NOT a general request-level cache (no caching of
 * document lists like PR/PO/GR — those need to reflect live status
 * changes right away) and deliberately short-TTL, so staleness is bounded
 * to a few seconds, not "forever until reload".
 *
 * Tenant/session safety: cleared on sign-out (see AuthContext.signOut) so
 * a second real login in the same browser tab never sees a prior
 * session's cached rows — matches "do not cache across tenant/branch
 * boundaries". Branch-scoped fetchers (e.g. listWarehouses(branchId))
 * must include the branchId in their cache key so switching the active
 * branch is itself a cache-key change, not a cache hit on stale data.
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

const DEFAULT_TTL_MS = 30_000;

export function cachedFetch<T>(key: string, fetcher: () => Promise<T>, ttlMs = DEFAULT_TTL_MS): Promise<T> {
  const hit = store.get(key);
  if (hit && hit.expiresAt > Date.now()) {
    return Promise.resolve(hit.data as T);
  }
  return fetcher().then((data) => {
    store.set(key, { data, expiresAt: Date.now() + ttlMs });
    return data;
  });
}

/** Call on sign-out (and ideally before a different user signs in in the
 * same tab) — never let cached rows from one session answer a request
 * made under a different session. */
export function clearCache(): void {
  store.clear();
}

/** Invalidate one cache key right after a write that changes it (e.g.
 * createItem() must invalidate 'items' or the just-created row wouldn't
 * appear until the TTL expires). Prefix match lets branch-scoped keys
 * (e.g. "warehouses:<branchId>") all be cleared with one call. */
export function invalidateCache(keyPrefix: string): void {
  for (const k of store.keys()) {
    if (k === keyPrefix || k.startsWith(`${keyPrefix}:`)) store.delete(k);
  }
}
